import axios from 'axios';
import { deserialize, serialize } from 'serializr';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { NotificationTypes } from '../../enums/notificationTypes';
import Notification from '../../shared/components/Notification/index';
import { isCancelError } from '../../shared/utils/isCancelError';
import { workOrderServiceMessages, workOrderServiceNotificationKeys, workOrderUploadPrefix } from './workOrder.constants';
import {
  DeleteWorkOrderAttachmentPayload,
  DeleteWorkOrderAttachmentResponse,
  DeleteWorkOrderResponse,
  EditWorkOrderPayload,
  UpdateWorkOrderStatusPayload,
  UpdateWorkOrderStatusResponse,
  WorkOrderDetails,
  WorkOrderPayload,
  WorkOrdersParams,
  WorkOrdersResponse,
} from '../../models/assetWorkOrder.model';
import type { WorkOrderSettings } from '../../types/InfrastructureManagerTypes';

export const notifyWorkOrderService = (type: NotificationTypes, message?: string, key?: string) => {
  Notification({
    message: message || workOrderServiceMessages.DEFAULT_ERROR,
    description: '',
    type,
    key,
  });
};

// Uploads straight to S3 and returns the S3 key the file was stored under. Bare axios for the PUT:
// the app's Authorization header would break the SigV4 signature (same reasoning as useFileUpload).
export const uploadWorkOrderFile = async (file: File): Promise<string> => {
  try {
    const fileType = file.name.split('.').pop()?.toLowerCase() ?? '';
    const key = `${workOrderUploadPrefix}/${crypto.randomUUID()}/${file.name}`;
    const { data } = await axiosInstance.post(ApiRoutes.PRESIGNED_URL, { fileName: key, fileType });
    await axios.put(data.presignedUrl.presignedUrl, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
    return key;
  } catch (error) {
    notifyWorkOrderService(NotificationTypes.ERROR, `${workOrderServiceMessages.UPLOAD_ERROR} (${file.name})`);
    throw error;
  }
};

export const fetchWorkOrders = async (params: WorkOrdersParams, signal?: AbortSignal): Promise<WorkOrdersResponse> => {
  try {
    const { status, ...rest } = params;
    const { data } = await axiosInstance.get(ApiRoutes.ASSET_MANAGER_WORK_ORDERS, {
      params: { ...rest, status: status?.length ? status.join(',') : undefined },
      signal,
    });
    return deserialize(WorkOrdersResponse, data);
  } catch (error) {
    if (!isCancelError(error)) {
      notifyWorkOrderService(
        NotificationTypes.ERROR,
        workOrderServiceMessages.GET_WORK_ORDERS_ERROR,
        workOrderServiceNotificationKeys.GET_WORK_ORDERS_ERROR,
      );
    }
    throw error;
  }
};

// The list endpoint caps `limit` at this, so "every work order" is read page by page.
const WORK_ORDERS_MAX_PAGE_SIZE = 200;

export const fetchAllWorkOrders = async (
  params: Omit<WorkOrdersParams, 'limit' | 'offset'>,
  signal?: AbortSignal,
): Promise<WorkOrdersResponse> => {
  const first = await fetchWorkOrders({ ...params, limit: WORK_ORDERS_MAX_PAGE_SIZE, offset: 0 }, signal);
  const workOrders = [...(first.workOrders ?? [])];
  let hasMore = !!first.pagination?.hasMore;
  while (hasMore) {
    const page = await fetchWorkOrders({ ...params, limit: WORK_ORDERS_MAX_PAGE_SIZE, offset: workOrders.length }, signal);
    const rows = page.workOrders ?? [];
    workOrders.push(...rows);
    hasMore = !!page.pagination?.hasMore && rows.length > 0;
  }
  return { ...first, workOrders };
};

export const fetchWorkOrderSettings = async (signal?: AbortSignal): Promise<WorkOrderSettings> => {
  const { data } = await axiosInstance.get(ApiRoutes.ASSET_MANAGER_WORK_ORDER_SETTINGS, { signal });
  return { zoomToLastHighlighted: data.zoomToLastHighlighted, statuses: data.statuses };
};

export const saveWorkOrderSettings = async (settings: WorkOrderSettings): Promise<WorkOrderSettings> => {
  const { data } = await axiosInstance.put(ApiRoutes.ASSET_MANAGER_WORK_ORDER_SETTINGS, settings);
  return { zoomToLastHighlighted: data.zoomToLastHighlighted, statuses: data.statuses };
};

export const fetchWorkOrder = async (id: number, signal?: AbortSignal): Promise<WorkOrderDetails> => {
  try {
    const { data } = await axiosInstance.get(ApiRoutes.ASSET_MANAGER_WORK_ORDER_BY_ID.replace(':id', String(id)), { signal });
    return deserialize(WorkOrderDetails, data);
  } catch (error) {
    if (!isCancelError(error)) {
      notifyWorkOrderService(
        NotificationTypes.ERROR,
        workOrderServiceMessages.GET_WORK_ORDER_ERROR,
        workOrderServiceNotificationKeys.GET_WORK_ORDER_ERROR,
      );
    }
    throw error;
  }
};

export const createWorkOrder = async (payload: WorkOrderPayload): Promise<WorkOrderDetails> => {
  const { data } = await axiosInstance.post(ApiRoutes.ASSET_MANAGER_WORK_ORDERS, serialize(WorkOrderPayload, payload));
  return deserialize(WorkOrderDetails, data);
};

export const saveWorkOrder = async ({ id, data: payload }: EditWorkOrderPayload): Promise<WorkOrderDetails> => {
  const { data } = await axiosInstance.put(ApiRoutes.ASSET_MANAGER_WORK_ORDER_BY_ID.replace(':id', String(id)), serialize(WorkOrderPayload, payload));
  return deserialize(WorkOrderDetails, data);
};

export const saveWorkOrderStatus = async ({ id, ...body }: UpdateWorkOrderStatusPayload): Promise<UpdateWorkOrderStatusResponse> => {
  const { data } = await axiosInstance.patch(ApiRoutes.ASSET_MANAGER_WORK_ORDER_STATUS.replace(':id', String(id)), body);
  return data;
};

export const removeWorkOrder = async (id: number): Promise<DeleteWorkOrderResponse> => {
  const { data } = await axiosInstance.delete(ApiRoutes.ASSET_MANAGER_WORK_ORDER_BY_ID.replace(':id', String(id)));
  return data;
};

export const removeWorkOrderAttachment = async ({ id, attachmentId }: DeleteWorkOrderAttachmentPayload): Promise<DeleteWorkOrderAttachmentResponse> => {
  const { data } = await axiosInstance.delete(
    ApiRoutes.ASSET_MANAGER_WORK_ORDER_ATTACHMENT.replace(':id', String(id)).replace(':attachmentId', String(attachmentId)),
  );
  return data;
};
